import { splitWineName } from "../MenuCMS/wineText";
import { selectionEntries, layerMembers } from "./studioGroups";
import { resolveStyle } from "./core/styles";
import { resolveText as coreResolveText, transformText as coreTransformText, wrapText as coreWrapText } from "./core/text";
import { validateDocument as coreValidateDocument } from "./core/validate";
// Menu Studio's independent document format. Coordinates and font sizes are points (72/in).
export const VERSION = 1;
export const uid = () =>
  `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 9)}`;
export const clone = (value) => JSON.parse(JSON.stringify(value));
export const ROLES = {
  title: "Restaurant title",
  subtitle: "Menu subtitle",
  section: "Section headings",
  dish: "Dish names",
  description: "Descriptions",
  price: "Prices",
  note: "Notes",
  decoration: "Artwork",
};
export const FONTS = [
  "Georgia",
  "Palatino",
  "Times New Roman",
  "Arial",
  "Verdana",
  "Courier New",
];
export const PAPER = {
  Letter: [612, 792],
  Legal: [612, 1008],
  A4: [595.28, 841.89],
  Tabloid: [792, 1224],
};
export const THEMES = {
  trattoria: {
    name: "Trattoria",
    description: "Classic serif · warm ivory · wine ink",
    paper: "#fffdf7",
    ink: "#382725",
    accent: "#864d39",
    font: "Georgia",
  },
  editorial: {
    name: "Editorial",
    description: "Expressive serif · crisp white · black",
    paper: "#ffffff",
    ink: "#222222",
    accent: "#222222",
    font: "Times New Roman",
  },
  garden: {
    name: "Garden",
    description: "Soft serif · natural paper · olive",
    paper: "#faf9f1",
    ink: "#354331",
    accent: "#68754d",
    font: "Palatino",
  },
};
export function defaultStyles(theme = THEMES.trattoria) {
  const base = {
    fontFamily: theme.font,
    fontSize: 10,
    fontWeight: 400,
    fontStyle: "normal",
    fill: theme.ink,
    align: "left",
    lineHeight: 1.25,
    letterSpacing: 0,
  };
  return Object.fromEntries(
    Object.keys(ROLES).map((role) => [
      role,
      {
        ...base,
        ...({
          title: { fontSize: 29, align: "center", letterSpacing: 1 },
          subtitle: {
            fontSize: 9,
            align: "center",
            letterSpacing: 3,
            fill: theme.accent,
          },
          section: { fontSize: 16, fill: theme.accent, letterSpacing: 0.6 },
          dish: { fontSize: 10.5, fontWeight: 700 },
          description: { fontSize: 9, fill: theme.ink },
          price: { fontSize: 10.5, align: "right" },
          note: { fontSize: 8, align: "center" },
        }[role] || {}),
      },
    ])
  );
}
export function element(role, text, x, y, w, h, extra = {}) {
  return {
    id: uid(),
    type: "text",
    name: text.slice(0, 60) || ROLES[role],
    role,
    text,
    x,
    y,
    w,
    h,
    rotation: 0,
    opacity: 1,
    style: {},
    ...extra,
  };
}
// Role style → named text style → element override (shared core).
export function getStyle(doc, node) {
  return resolveStyle(doc, node);
}
export function priceText(item) {
  const prices = (Array.isArray(item.price) ? item.price : [item.price]).filter(
    (p) => p !== undefined && p !== null
  );
  if (!prices.length) return "market price";
  return prices
    .map((p) => Number(p).toFixed(2).replace(/\.00$/, ""))
    .join(" / ");
}

export function contentIndex(menus) {
  const map = {};
  Object.entries(menus || {}).forEach(([key, menu]) => { if (Array.isArray(menu?.sections)) map[`menu:${key}:loaded`] = true; });
  Object.values(menus || {}).forEach((menu) =>
    (menu?.sections || []).forEach((section) => {
      map[`section:${section._id}:header`] = section.header || "";
      if (section.updatedAt) map[`updated:section:${section._id}`] = section.updatedAt;
      map[`section:${section._id}:description`] = section.description || "";
      (section.items || []).forEach((item) => {
        map[`item:${item._id}:name`] = item.name || "";
        if (item.updatedAt) map[`updated:item:${item._id}`] = item.updatedAt;
        const wine = splitWineName(item.name);
        map[`item:${item._id}:wineLabel`] = wine.label;
        map[`item:${item._id}:wineOrigin`] = wine.origin;
        map[`item:${item._id}:wineType`] = wine.type;
        map[`item:${item._id}:wineMaker`] = wine.maker;
        map[`item:${item._id}:wineMakerOrigin`] = wine.makerOrigin;
        map[`item:${item._id}:description`] = item.description || "";
        const sauces = (item.sauces || []).filter(s => s.name?.trim());
        const sizes = (item.sizes || []).filter(s => s.name?.trim());
        map[`item:${item._id}:sauceNames`] = sauces.map(s => s.name).join("\n");
        map[`item:${item._id}:sizeLabels`] = sizes.map(s => s.name).join(" / ");
        map[`item:${item._id}:saucePrices`] = sauces.map(sauce =>
          (sizes.length ? sizes : [{ price: 0 }]).map(size =>
            (Number(sauce.price || 0) + Number(size.price || 0)).toFixed(2)
          ).join(" / ")
        ).join("\n");
        map[`item:${item._id}:saucePricesCompact`] = sauces.map(sauce =>
          (sizes.length ? sizes : [{ price: 0 }]).map(size => {
            const amount = Number(sauce.price || 0) + Number(size.price || 0);
            return amount.toFixed(2).replace(/\.00$/, "");
          }).join(" / ")
        ).join("\n");
        map[`item:${item._id}:price`] = priceText(item);
        map[`item:${item._id}:rawPrices`] = JSON.stringify(item.price);
        map[`item:${item._id}:sizes`] = JSON.stringify(
          (item.sizes || []).map((s) => s.name)
        );
        map[`item:${item._id}:modifiers`] = [
          "options",
          "sauces",
          "pastas",
          "platters",
        ]
          .flatMap((key) =>
            (item[key] || [])
              .filter((v) => v.name?.trim())
              .map((v) => `${v.name}${v.price ? ` +${v.price}` : ""}`)
          )
          .join(" · ");
      });
    })
  );
  return map;
}
export const resolveText = coreResolveText;

export const transformText = coreTransformText;

export function textStylePatch(node, changes) {
  const patch = { style: { ...node.style, ...changes } };
  if (changes.textTransform !== undefined) {
    patch.originalLayout = undefined;
    // Regular case follows the current CMS spelling, not an imported snapshot.
    if (node.binding && node.role !== "price") patch.preserveOriginal = false;
  }
  return patch;
}

export const wrapText = coreWrapText;
export function generateDocument(
  menus,
  menuKey = "dinner",
  themeKey = "trattoria",
  columns = 2
) {
  const theme = THEMES[themeKey];
  const doc = {
    version: VERSION,
    name: `Trattoria Demi · ${
      menuKey === "wine"
        ? "Beverages"
        : menuKey === "dinner" ? "Full Menu"
        : menuKey.charAt(0).toUpperCase() + menuKey.slice(1)
    }`,
    menuKey,
    styles: defaultStyles(theme),
    pages: [],
    themeKey,
    assets: [],
  };
  const width = 612,
    height = 792,
    margin = 40,
    gap = 24,
    colW = (width - margin * 2 - gap * (columns - 1)) / columns;
  let page, col, y;
  function newPage() {
    page = {
      id: uid(),
      name: `Page ${doc.pages.length + 1}`,
      width,
      height,
      margin,
      background: theme.paper,
      elements: [],
    };
    doc.pages.push(page);
    col = 0;
    y = 145;
    page.elements.push(
      element("title", "TRATTORIA DEMI", margin, 40, width - margin * 2, 42)
    );
    page.elements.push(
      element(
        "subtitle",
        menuKey === "wine" ? "BEVERAGES" : `${menuKey.toUpperCase()} MENU`,
        margin,
        86,
        width - margin * 2,
        18
      )
    );
    page.elements.push(
      element("decoration", "", margin, 118, width - margin * 2, 1, {
        type: "line",
        name: "Header rule",
        style: { fill: theme.accent, strokeWidth: 0.7 },
      })
    );
    page.elements.push(
      element(
        "note",
        "TRATTORIA DEMI  ·  EVANSTON",
        margin,
        height - 30,
        width - margin * 2,
        12
      )
    );
  }
  function nextColumn() {
    if (col + 1 >= columns) newPage();
    else {
      col++;
      y = 145;
    }
  }
  newPage();
  const sourceKey = menuKey === "takeout" ? "dinner" : menuKey;
  const sections = (menus?.[sourceKey]?.sections || []).filter(
    (s) => !["dinner", "takeout"].includes(menuKey) || String(s.header).toUpperCase() !== "BEER"
  );
  if (menuKey === "wine")
    sections.unshift(
      ...(menus?.dinner?.sections || []).filter(
        (s) => String(s.header).toUpperCase() === "BEER"
      )
    );
  sections.forEach((section) => {
    if (y > height - 160) nextColumn();
    let groupId = uid();
    const addHeading = (continued) => {
      const x = margin + col * (colW + gap);
      page.elements.push(
        element(
          "section",
          `${section.header}${continued ? " (continued)" : ""}`,
          x,
          y,
          colW,
          25,
          {
            sectionId: section._id,
            groupId,
            binding: continued ? undefined : `section:${section._id}:header`,
          }
        )
      );
      y += 30;
    };
    addHeading(false);
    if (section.description) {
      const h =
        wrapText(section.description, doc.styles.description, colW).length *
          12 +
        5;
      page.elements.push(
        element(
          "description",
          section.description,
          margin + col * (colW + gap),
          y,
          colW,
          h,
          {
            sectionId: section._id,
            groupId,
            binding: `section:${section._id}:description`,
          }
        )
      );
      y += h + 6;
    }
    (section.items || []).forEach((item) => {
      const price = priceText(item),
        priceW = Math.min(colW * 0.47, Math.max(40, price.length * 5.6));
      const nameH = Math.max(
        15,
        wrapText(item.name, doc.styles.dish, colW - priceW - 10).length * 14
      );
      const priceH = Math.max(
        15,
        wrapText(price, doc.styles.price, priceW).length * 14
      );
      const desc = item.description || "";
      const modifiers = contentIndex({
        m: { sections: [{ ...section, items: [item] }] },
      })[`item:${item._id}:modifiers`];
      const descH = desc
        ? wrapText(desc, doc.styles.description, colW).length * 12 + 3
        : 0;
      const modH = modifiers
        ? wrapText(modifiers, doc.styles.description, colW).length * 12 + 3
        : 0;
      const rowH = Math.max(nameH, priceH) + descH + modH + 12;
      if (y + rowH > height - 55) {
        nextColumn();
        groupId = uid();
        addHeading(true);
      }
      const x = margin + col * (colW + gap),
        meta = { sectionId: section._id, itemId: item._id, groupId };
      page.elements.push(
        element("dish", item.name || "", x, y, colW - priceW - 10, nameH, {
          ...meta,
          binding: `item:${item._id}:name`,
        })
      );
      page.elements.push(
        element("price", price, x + colW - priceW, y, priceW, priceH, {
          ...meta,
          binding: `item:${item._id}:price`,
        })
      );
      y += Math.max(nameH, priceH);
      if (desc) {
        page.elements.push(
          element("description", desc, x, y, colW, descH, {
            ...meta,
            binding: `item:${item._id}:description`,
          })
        );
        y += descH;
      }
      if (modifiers) {
        page.elements.push(
          element("description", modifiers, x, y, colW, modH, {
            ...meta,
            binding: `item:${item._id}:modifiers`,
            style: { fontStyle: "italic" },
          })
        );
        y += modH;
      }
      y += 12;
    });
    y += 16;
  });
  return doc;
}
export function bounds(nodes) {
  if (!nodes.length) return null;
  const x = Math.min(...nodes.map((n) => n.x)),
    y = Math.min(...nodes.map((n) => n.y));
  return {
    x,
    y,
    w: Math.max(...nodes.map((n) => n.x + n.w)) - x,
    h: Math.max(...nodes.map((n) => n.y + n.h)) - y,
  };
}
function sameArtwork(doc, a, b) {
  if (a.src && a.src === b.src) return true;
  if (a.artworkSource && b.artworkSource) return a.artworkSource === b.artworkSource;
  // Legacy PDF imports baked flips/rotations into the pixels, before source
  // identity was stored. Retain the two known ornament families in those drafts.
  const legacyOrnaments = ["Original corner ornament", "Original flourish"];
  return doc.source === "original-pdf" && !a.artworkSource && !b.artworkSource &&
    legacyOrnaments.includes(a.name) && a.name === b.name;
}

export function matchingIds(doc, node, scope, pageId, selected) {
  if (scope === "selection") return selected;
  return doc.pages.flatMap((p) =>
    p.elements
      .filter(
        (n) =>
          n.role === node.role &&
          n.type === node.type &&
          (node.type !== "image" || sameArtwork(doc, node, n)) &&
          !n.locked &&
          (scope !== "page" || p.id === pageId) &&
          (scope !== "textStyle" || (node.styleRef && n.styleRef === node.styleRef)) &&
          (scope !== "section" ||
            (node.sectionId && n.sectionId === node.sectionId))
      )
      .map((n) => n.id)
  );
}
export function followAttachedArtwork(before, after) {
  const old = new Map(before.pages.flatMap((p) => p.elements).map((n) => [n.id, n]));
  const updated = new Map(after.pages.flatMap((p) => p.elements).map((n) => [n.id, n]));
  return { ...after, pages: after.pages.map((p) => ({ ...p, elements: p.elements.map((n) => {
    const previous = old.get(n.id), anchor = updated.get(n.attachedTo), oldAnchor = old.get(n.attachedTo);
    if (!previous || !anchor || !oldAnchor || n.x !== previous.x || n.y !== previous.y) return n;
    const dx = anchor.x - oldAnchor.x, dy = anchor.y - oldAnchor.y;
    // A no-op content sync must not accumulate floating-point drift or mark
    // unchanged pages as edited when the design is reopened.
    return dx || dy ? { ...n, x: n.x + dx, y: n.y + dy } : n;
  }) })) };
}
export function patchElements(doc, ids, patch) {
  const set = new Set(ids);
  return followAttachedArtwork(doc, {
    ...doc,
    pages: doc.pages.map((p) => ({
      ...p,
      elements: p.elements.map((n) =>
        set.has(n.id)
          ? { ...n, ...(typeof patch === "function" ? patch(n) : patch) }
          : n
      ),
    })),
  });
}
export function alignElements(doc, pageId, ids, direction) {
  const page = doc.pages.find((p) => p.id === pageId),
    nodes = page.elements.filter((n) => ids.includes(n.id) && !n.locked);
  const box =
    nodes.length === 1
      ? {
          x: page.margin,
          y: page.margin,
          w: page.width - page.margin * 2,
          h: page.height - page.margin * 2,
        }
      : bounds(nodes);
  if (!box) return doc;
  return patchElements(
    doc,
    nodes.map((n) => n.id),
    (n) => ({
      ...{
        left: { x: box.x },
        center: { x: box.x + (box.w - n.w) / 2 },
        right: { x: box.x + box.w - n.w },
        top: { y: box.y },
        middle: { y: box.y + (box.h - n.h) / 2 },
        bottom: { y: box.y + box.h - n.h },
      }[direction],
    })
  );
}
// Complete groups are layout units; partial selections remain individual layers.
export function distributionUnits(page, ids, focusId) {
  if (!page) return [];
  return selectionEntries(page, ids, focusId).map((entry) => {
    const members = layerMembers(entry);
    return { members, ...bounds(members) };
  }).filter((unit) => !unit.members.some((node) => node.locked));
}
export function distributeElements(doc, pageId, ids, axis, focusId) {
  const page = doc.pages.find((p) => p.id === pageId);
  const units = distributionUnits(page, ids, focusId).sort((a, b) => a[axis] - b[axis]);
  if (units.length < 3) return doc;
  const dim = axis === "x" ? "w" : "h", first = units[0], last = units[units.length - 1];
  const gap = (last[axis] + last[dim] - first[axis] - units.reduce((sum, unit) => sum + unit[dim], 0)) / (units.length - 1);
  let pos = first[axis];
  const positions = new Map();
  units.forEach((unit, index) => {
    // Preserve both endpoints exactly, including their internal geometry.
    const delta = index === 0 || index === units.length - 1 ? 0 : pos - unit[axis];
    unit.members.forEach((node) => positions.set(node.id, node[axis] + delta));
    pos += unit[dim] + gap;
  });
  return patchElements(doc, [...positions.keys()], (node) => ({ [axis]: positions.get(node.id) }));
}
// One validator for the editor, API and CLI (shared core).
export function validateDocument(doc) {
  return coreValidateDocument(doc);
}

export function applyTypography(doc, node, scope, pageId, selected, changes) {
  // A named text style: change the one record; members inherit it. Locked
  // members keep their current look through an explicit override.
  if (scope === "textStyle" && node.styleRef && doc.textStyles?.[node.styleRef]) {
    const record = doc.textStyles[node.styleRef];
    const next = { ...doc, textStyles: { ...doc.textStyles, [node.styleRef]: { ...record, style: { ...record.style, ...changes } } } };
    return { ...next, pages: doc.pages.map((p) => ({ ...p, elements: p.elements.map((n) => {
      if (n.styleRef !== node.styleRef) return n;
      const style = { ...n.style };
      Object.keys(changes).forEach((key) => { if (n.locked) style[key] = getStyle(doc, n)[key]; else delete style[key]; });
      return { ...n, ...(!n.locked ? textStylePatch(n, changes) : {}), style };
    }) })) };
  }
  const ids = new Set(matchingIds(doc, node, scope, pageId, selected));
  if (scope !== "all" || node.type !== "text")
    return patchElements(
      doc,
      [...ids].filter(
        (id) =>
          !doc.pages.flatMap((p) => p.elements).find((n) => n.id === id)?.locked
      ),
      (n) => textStylePatch(n, changes)
    );
  const next = {
    ...doc,
    styles: {
      ...doc.styles,
      [node.role]: { ...doc.styles[node.role], ...changes },
    },
  };
  next.pages = doc.pages.map((p) => ({
    ...p,
    elements: p.elements.map((n) => {
      if (n.role !== node.role) return n;
      const style = { ...n.style };
      Object.keys(changes).forEach((key) => {
        if (n.locked) style[key] = getStyle(doc, n)[key];
        else delete style[key];
      });
      return { ...n, ...(!n.locked ? textStylePatch(n, changes) : {}), style };
    }),
  }));
  return next;
}

export function validFonts(fonts) {
  return (
    fonts === undefined ||
    (Array.isArray(fonts) &&
      fonts.length <= 30 &&
      fonts.every(
        (f) =>
          /^[a-zA-Z0-9 _-]{1,100}$/.test(f.family) &&
          /^(normal|italic)$/.test(f.style) &&
          /^\d{3}( \d{3})?$/.test(String(f.weight)) &&
          /^data:font\/woff2;base64,[A-Za-z0-9+/=]+$/.test(f.src)
      ))
  );
}
